import React, { useEffect, useRef, useState } from "react";
import { Button, FluentProvider, webLightTheme } from "@fluentui/react-components";
import { CallEnd20Filled, Call20Filled, Mic20Regular, MicOff20Regular } from "@fluentui/react-icons";
import type { IContact } from "../../../../types/Contacts";
import { calls, useCalls } from "../spurt/calls";
import { PersonAvatar, fmtTimer } from "../Shared/Teamsshared";

/** Remote audio must keep playing when the chat window is closed or another page is open, so it lives here, at the
 *  root, not inside the Calls page. Video elements for the Calls page use <StreamVideo>. */
const RemoteAudio: React.FC<{ streams: MediaStream[]; silent: boolean }> = ({ streams, silent }) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const host = ref.current;
    if (!host) return;
    const els: HTMLAudioElement[] = streams
      .filter((s) => s.getAudioTracks().length > 0)
      .map((s) => {
        const a = document.createElement("audio");
        a.autoplay = true;
        a.muted = silent;
        a.srcObject = s;
        host.appendChild(a);
        void a.play().catch(() => undefined);
        return a;
      });
    return () => els.forEach((a) => {
      a.srcObject = null;
      a.remove();
    });
  }, [streams, silent]);
  return <div ref={ref} aria-hidden="true" style={{ display: "none" }} />;
};

export const StreamVideo: React.FC<{ stream: MediaStream | null; mirror?: boolean; className?: string | undefined; label: string }> = ({ stream, mirror, className, label }) => {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const v = ref.current;
    if (v) v.srcObject = stream;
  }, [stream]);
  return <video ref={ref} className={className} aria-label={label} autoPlay playsInline muted style={{ width: "100%", background: "#111", borderRadius: 8, transform: mirror ? "scaleX(-1)" : undefined }} />;
};

const useTick = (on: boolean): number => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!on) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [on]);
  return now;
};

interface Props {
  windowOpen: boolean;
  page: string;
  onOpen: () => void;
  onShowCall: () => void;
  contactById: Record<string, IContact>;
  toast: (m: string) => void;
}

const CallOverlay: React.FC<Props> = ({ windowOpen, page, onShowCall, contactById, toast }) => {
  const incoming = useCalls((s) => s.incoming);
  const active = useCalls((s) => s.active);
  const phase = useCalls((s) => s.phase);
  const streams = useCalls((s) => s.streams);
  const error = useCalls((s) => s.error);
  const now = useTick(!!active);

  useEffect(() => {
    if (!error) return;
    toast(error);
    calls().clearError();
  }, [error, toast]);

  const ringer = incoming ? contactById[incoming.contactId] : undefined;
  const showBar = !!active && !(windowOpen && page === "calls");

  return (
    <FluentProvider theme={webLightTheme} style={{ display: "contents" }}>
      <RemoteAudio streams={streams} silent={!!active?.hold} />

      {incoming && (
        <div role="alertdialog" aria-label={`Incoming call from ${incoming.name}`} style={{ position: "fixed", top: 16, right: 16, zIndex: 2147483001, width: 320, background: "#fff", borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,.28)", padding: 16, display: "grid", gap: 12 }}>
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <PersonAvatar name={incoming.name} image={ringer?.profilePicture} size={48} />
            <div>
              <div style={{ fontWeight: 600 }}>{incoming.name}</div>
              <div style={{ color: "#616161", fontSize: 13 }}>Incoming call…</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Button appearance="primary" icon={<Call20Filled />} onClick={() => { onShowCall(); calls().accept(false).catch((e: Error) => toast(e.message)); }}>
              Answer
            </Button>
            <Button icon={<CallEnd20Filled />} onClick={() => calls().decline()}>
              Decline
            </Button>
          </div>
        </div>
      )}

      {showBar && active && (
        <div role="region" aria-label="Call in progress" style={{ position: "fixed", left: "50%", bottom: 16, transform: "translateX(-50%)", zIndex: 2147483001, background: "#242424", color: "#fff", borderRadius: 28, padding: "8px 8px 8px 16px", display: "flex", alignItems: "center", gap: 12, boxShadow: "0 8px 24px rgba(0,0,0,.35)" }}>
          <button type="button" onClick={onShowCall} style={{ all: "unset", cursor: "pointer", display: "flex", alignItems: "center", gap: 10 }} aria-label="Show call">
            <PersonAvatar name={active.name} image={contactById[active.contactId]?.profilePicture} size={28} />
            <span style={{ fontWeight: 600 }}>{active.name}</span>
            <span style={{ opacity: 0.8 }}>{active.connectedAt ? fmtTimer(Math.max(0, Math.floor((now - active.connectedAt) / 1000))) : phase === "connecting" ? "Connecting…" : "Calling…"}</span>
          </button>
          <Button shape="circular" appearance="subtle" aria-pressed={active.muted} aria-label={active.muted ? "Unmute" : "Mute"} icon={active.muted ? <MicOff20Regular /> : <Mic20Regular />} onClick={() => calls().toggleMute()} style={{ color: "#fff" }} />
          <Button shape="circular" appearance="primary" aria-label="End call" icon={<CallEnd20Filled />} onClick={() => void calls().end("local")} style={{ background: "#c4314b" }} />
        </div>
      )}
    </FluentProvider>
  );
};

export default CallOverlay;
